import {
  type CollectionReference,
  type DocumentData,
  type QueryDocumentSnapshot,
  collection,
  orderBy,
  query,
} from 'firebase/firestore'
import { type UploadTask, ref, uploadBytesResumable } from 'firebase/storage'

import { i18n } from '@/i18n'
import { callable } from '@/lib/firebase/callable'
import { getFirebaseFirestore, getFirebaseStorage } from '@/lib/firebase/app'
import { parseCallableError } from '@/lib/firebase/errors'
import { type Subscription, subscribeCollection } from '@/lib/firebase/subscribe'
import type {
  LibraryItem,
  LibraryItemBrandRole,
  LibraryItemContentType,
  LibraryItemGround,
} from '@/types/library'

export interface LibraryScope {
  workspaceId: string
  brandId: string
}

const ALLOWED_LIBRARY_CONTENT_TYPES: readonly LibraryItemContentType[] = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]

function libraryItemsCollectionRef(scope: LibraryScope): CollectionReference<DocumentData> {
  return collection(
    getFirebaseFirestore(),
    'workspaces',
    scope.workspaceId,
    'brands',
    scope.brandId,
    'libraryItems',
  )
}

const ALLOWED_BRAND_ROLES: readonly LibraryItemBrandRole[] = ['logo', 'monogram', 'backdrop']
const ALLOWED_GROUNDS: readonly LibraryItemGround[] = ['light', 'dark']

function isAllowedContentType(value: unknown): value is LibraryItemContentType {
  return (
    typeof value === 'string' &&
    (ALLOWED_LIBRARY_CONTENT_TYPES as readonly string[]).includes(value)
  )
}

function isBrandRole(value: unknown): value is LibraryItemBrandRole {
  return typeof value === 'string' && (ALLOWED_BRAND_ROLES as readonly string[]).includes(value)
}

function isGround(value: unknown): value is LibraryItemGround {
  return typeof value === 'string' && (ALLOWED_GROUNDS as readonly string[]).includes(value)
}

function toLibraryItem(snapshot: QueryDocumentSnapshot<DocumentData>): LibraryItem | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  return {
    id: snapshot.id,
    kind: 'image',
    filename: typeof data.filename === 'string' ? data.filename : '',
    contentType: isAllowedContentType(data.contentType) ? data.contentType : 'image/png',
    sizeBytes: typeof data.sizeBytes === 'number' ? data.sizeBytes : 0,
    storagePath: typeof data.storagePath === 'string' ? data.storagePath : '',
    width: typeof data.width === 'number' ? data.width : 0,
    height: typeof data.height === 'number' ? data.height : 0,
    createdAt: typeof data.createdAt === 'string' ? data.createdAt : '',
    source: data.source === 'brand' ? 'brand' : 'upload',
    ...(isBrandRole(data.brandRole) ? { brandRole: data.brandRole } : {}),
    ...(isGround(data.ground) ? { ground: data.ground } : {}),
    ...(typeof data.sourceUrl === 'string' ? { sourceUrl: data.sourceUrl } : {}),
  }
}

export function subscribeLibraryItems(scope: LibraryScope): Subscription<LibraryItem[]> {
  return subscribeCollection(
    query(libraryItemsCollectionRef(scope), orderBy('createdAt', 'desc')),
    (snapshot) =>
      snapshot.docs
        .map((doc) => toLibraryItem(doc))
        .filter((item): item is LibraryItem => item !== undefined),
  )
}

export interface PrepareLibraryUploadInput {
  filename: string
  contentType: string
  sizeBytes: number
}

export interface PrepareLibraryUploadResult {
  itemId: string
  uploadPath: string
}

const prepareLibraryUpload = callable<PrepareLibraryUploadInput, PrepareLibraryUploadResult>(
  'prepareLibraryUpload',
)

export interface FinalizeLibraryUploadInput {
  itemId: string
  filename: string
}

export interface FinalizeLibraryUploadResult {
  item: LibraryItem
}

const finalizeLibraryUpload = callable<FinalizeLibraryUploadInput, FinalizeLibraryUploadResult>(
  'finalizeLibraryUpload',
)

export interface DeleteLibraryItemInput {
  itemId: string
}

export const deleteLibraryItem = callable<DeleteLibraryItemInput, void>('deleteLibraryItem')

const LIBRARY_UPLOAD_ERROR_MESSAGE_KEYS: Record<string, string> = {
  content_type_not_allowed: 'media.uploadError.contentTypeNotAllowed',
  file_too_large: 'media.uploadError.fileTooLarge',
  brand_storage_quota_exceeded: 'media.uploadError.storageQuotaExceeded',
  unreadable_image_dimensions: 'media.uploadError.unreadableImage',
  upload_not_found: 'media.uploadError.uploadNotFound',
}

function toLibraryUploadErrorMessage(cause: unknown): string {
  const parsed = parseCallableError(cause)
  const key = parsed.reason ? LIBRARY_UPLOAD_ERROR_MESSAGE_KEYS[parsed.reason] : undefined
  return i18n.global.t(key ?? 'media.uploadError.fallback')
}

export class LibraryUploadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LibraryUploadError'
  }
}

export interface UploadLibraryItemInput {
  file: File
  onProgress?: (fraction: number) => void
}

export interface UploadLibraryItemHandle {
  result: Promise<LibraryItem>
  cancel: () => void
}

/** Uploads a file end to end: reserve, upload bytes, finalize. Reports progress, supports cancellation. */
export function uploadLibraryItem(input: UploadLibraryItemInput): UploadLibraryItemHandle {
  const { file, onProgress } = input
  let uploadTask: UploadTask | undefined
  let cancelled = false

  const result = (async (): Promise<LibraryItem> => {
    let itemId: string
    let uploadPath: string
    try {
      const prepared = await prepareLibraryUpload({
        filename: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      })
      itemId = prepared.itemId
      uploadPath = prepared.uploadPath
    } catch (cause) {
      throw new LibraryUploadError(toLibraryUploadErrorMessage(cause))
    }

    if (cancelled) {
      throw new LibraryUploadError(i18n.global.t('media.uploadError.fallback'))
    }

    await new Promise<void>((resolve, reject) => {
      const task = uploadBytesResumable(ref(getFirebaseStorage(), uploadPath), file, {
        contentType: file.type,
      })
      uploadTask = task
      task.on(
        'state_changed',
        (snapshot) => {
          onProgress?.(snapshot.bytesTransferred / snapshot.totalBytes)
        },
        (error) => {
          reject(new LibraryUploadError(toLibraryUploadErrorMessage(error)))
        },
        () => {
          resolve()
        },
      )
    })

    try {
      const finalized = await finalizeLibraryUpload({ itemId, filename: file.name })
      return finalized.item
    } catch (cause) {
      throw new LibraryUploadError(toLibraryUploadErrorMessage(cause))
    }
  })()

  return {
    result,
    cancel: () => {
      cancelled = true
      uploadTask?.cancel()
    },
  }
}
